import { Check, CircleHelp, Table2, TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { getErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import {
  STATEMENT_IDENTITY,
  useGetAiScriptStatement,
} from "@/payroll/ai-script/query";
import { statementRowKey } from "@/payroll/ai-script/statement/statement-renderer";
import { useCloseManagedPackThread } from "@/payroll/pack-chat/manager-query";
import { PackChatStatusMark } from "@/payroll/pack-chat/pack-chat-status-mark";
import {
  isGeneralPayPackContext,
  referenceParts,
} from "@/payroll/pack-chat/reference-label";
import { getStatementItemReferences } from "@/payroll/pack-chat/statement-items";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";
import { getPayBreakdownSheetPathForName } from "@/payroll/pay-breakdown/routes";

export const PACK_CHAT_CONVERSATION_COLUMN =
  "mx-auto w-full min-w-0 max-w-[64rem]";

/** The fixed manager header for one PayPack conversation. */
export const PackChatConversationHeader = ({
  businessId,
  periodStart,
  runId,
  thread,
  technicianName,
}: {
  businessId: string | null;
  periodStart: string;
  runId: string | null;
  thread: IPackChatThreadSummary;
  technicianName: string;
}) => {
  const close = useCloseManagedPackThread(businessId, periodStart);
  const currentRunId = runId ?? thread.ai_script_run_id;
  const isGeneral = isGeneralPayPackContext(thread.context);
  const packQuery = useGetAiScriptStatement(businessId, currentRunId, {
    identity: STATEMENT_IDENTITY.technician,
    technicianId: thread.technician.id,
    enabled: !isGeneral && Boolean(businessId && currentRunId),
  });
  const pack = packQuery.data?.statement;
  const packAmounts = useMemo(
    () =>
      new Map(
        getStatementItemReferences(pack).map((reference) => [
          statementRowKey(reference),
          reference.amount,
        ]),
      ),
    [pack],
  );

  const { path, label, value, valueIsFigure } = referenceParts(thread.context);
  const contextKey = statementRowKey(thread.context);
  const currentAmount = packAmounts.get(contextKey);
  const figureMissing =
    !isGeneral && Boolean(pack) && !packAmounts.has(contextKey);
  const amountChanged =
    currentAmount !== undefined &&
    Boolean(value) &&
    valueIsFigure &&
    currentAmount !== value;
  const subject = [path, label, value && valueIsFigure ? value : null]
    .filter(Boolean)
    .join(" · ");
  const messageLabel = `${thread.message_count} ${
    thread.message_count === 1 ? "message" : "messages"
  }`;
  const sheetName = thread.source_sheet?.name;
  const sheetHref = sheetName
    ? getPayBreakdownSheetPathForName(periodStart, sheetName)
    : null;
  const open = thread.status === "open";

  const closeThread = async () => {
    try {
      await close.mutateAsync(thread.id);
      toast.success("Thread closed");
    } catch (error) {
      toast.error("Thread not closed", {
        description: getErrorMessage(error),
      });
    }
  };

  return (
    <Stack
      gap="none"
      align="stretch"
      fullWidth
      className="shrink-0 border-b border-stroke-tertiary px-5 py-4"
    >
      <Stack
        direction="row"
        align="start"
        justify="between"
        gap="md"
        fullWidth
        className={PACK_CHAT_CONVERSATION_COLUMN}
      >
        <Stack gap="none" align="stretch" className="min-w-0">
          <Stack direction="row" align="center" gap="xs" className="min-w-0">
            <Typography variant="subtitleW2" className="min-w-0 truncate">
              {technicianName}
            </Typography>
            {sheetHref && sheetName && (
              <Tooltip>
                <TooltipTrigger asDiv className="inline-flex">
                  <Link
                    to={sheetHref}
                    className="shrink-0 rounded p-0.5 text-tertiary-text transition-colors hover:bg-surface-secondary hover:text-primary-text"
                  >
                    <Table2 className="size-4" aria-hidden />
                    <span className="sr-only">
                      Open {sheetName} in the Grid
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" maxWidth="sm">
                  Open the {sheetName} sheet in the Grid, where this pay was
                  worked out.
                </TooltipContent>
              </Tooltip>
            )}
          </Stack>

          <Stack
            direction="row"
            align="baseline"
            gap="none"
            className="min-w-0 flex-wrap gap-x-1.5"
          >
            <Typography variant="bodyW3" className="min-w-0 text-[13px]">
              <span className="text-tertiary-text">{messageLabel}</span>
              {subject && (
                <span className="font-medium text-secondary-text">
                  {" · "}
                  {subject}
                </span>
              )}
              {amountChanged && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className="cursor-pointer whitespace-nowrap text-tertiary-text"
                    >
                      {" "}
                      when asked
                      <CircleHelp
                        className="ml-1 inline size-3 align-[-0.125em]"
                        aria-hidden
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" maxWidth="md">
                    {value} is what this figure read when the question was
                    asked. The pack now shows {currentAmount}.
                  </TooltipContent>
                </Tooltip>
              )}
            </Typography>

            {figureMissing && (
              <Tooltip>
                <TooltipTrigger asDiv className="shrink-0">
                  <span className="cursor-pointer whitespace-nowrap text-[13px] font-medium text-gold-70">
                    <TriangleAlert
                      className="mr-1 inline size-3.5 align-[-0.1875em]"
                      aria-hidden
                    />
                    No longer on this pack
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" maxWidth="md">
                  This figure was on the pack when the question was asked. A
                  later run renamed or removed it, so the pack no longer has the
                  row. The question and its answers are unchanged.
                </TooltipContent>
              </Tooltip>
            )}
          </Stack>

          <Typography
            variant="bodyW3"
            className="text-[13px] text-tertiary-text"
          >
            {dayjs(thread.updated_at).fromNow()}
          </Typography>
        </Stack>

        <Stack gap="sm" align="end" className="shrink-0">
          <PackChatStatusMark
            open={open}
            label={open ? "Open" : "Closed"}
            hasAiMessages={thread.has_ai_messages}
            needsManager={thread.needs_manager}
            responseOwner={thread.response_owner}
          />
          {open && (
            <button
              type="button"
              disabled={close.isPending}
              onClick={() => void closeThread()}
              className="inline-flex shrink-0 items-center gap-1.5 pt-1 text-[13px] text-secondary-text transition-colors hover:text-primary-text disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Check className="size-3.5 shrink-0" aria-hidden />
              {close.isPending ? "Closing…" : "Close thread"}
            </button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
